import React from 'react';
import { Alert, Box, Button, Chip, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import type { FoodTestId, ProcedureAction, ProcedureStep } from './types';

export const foodTestFlows: Record<FoodTestId, Array<{ action: ProcedureAction; label: string; detail: string }>> = {
  'reducing-sugar': [
    { action: 'add-sample', label: 'Measure the food sample', detail: 'Add about 2 cm³ of sample to a clean test tube.' },
    { action: 'add-benedicts', label: 'Add Benedict’s solution', detail: 'Add an approximately equal volume of blue Benedict’s solution.' },
    { action: 'heat-water-bath', label: 'Place in the water bath', detail: 'Use a near-boiling water bath; do not heat the tube directly.' },
    { action: 'wait-five-minutes', label: 'Heat for five minutes', detail: 'Start the simulated five-minute incubation and observe the colour ladder.' },
  ],
  'non-reducing-sugar': [
    { action: 'add-sample', label: 'Measure the food sample', detail: 'Add about 2 cm³ of sample to a clean test tube.' },
    { action: 'add-dilute-hcl', label: 'Add dilute hydrochloric acid', detail: 'Acid is needed to hydrolyse the non-reducing sugar.' },
    { action: 'heat-water-bath', label: 'Heat in a water bath', detail: 'Warm the acidic sample in a near-boiling water bath.' },
    { action: 'wait-five-minutes', label: 'Hydrolyse for five minutes', detail: 'Allow time for sucrose to hydrolyse into reducing sugars.' },
    { action: 'neutralise-nahco3', label: 'Neutralise with sodium hydrogen carbonate', detail: 'Add carefully; avoid excess and fizzing over the tube.' },
    { action: 'check-neutral-ph', label: 'Check for neutral pH', detail: 'Confirm approximately pH 6–8 before adding Benedict’s.' },
    { action: 'add-benedicts', label: 'Add Benedict’s solution', detail: 'Add an approximately equal volume after neutralisation.' },
    { action: 'heat-water-bath', label: 'Return to the water bath', detail: 'Use the water bath, not a direct flame.' },
    { action: 'wait-five-minutes', label: 'Heat for five minutes', detail: 'Observe the final Benedict’s colour.' },
  ],
  starch: [
    { action: 'add-sample', label: 'Place the food sample', detail: 'Use a small sample in a tube or on a clean white tile.' },
    { action: 'add-iodine', label: 'Add iodine solution', detail: 'Add a few drops at room temperature; do not heat.' },
  ],
  protein: [
    { action: 'add-sample', label: 'Measure the food sample', detail: 'Add about 2 cm³ of sample to a clean test tube.' },
    { action: 'add-sodium-hydroxide', label: 'Make the sample alkaline', detail: 'Add sodium hydroxide first. It is corrosive; use eye protection.' },
    { action: 'add-copper-sulfate', label: 'Add copper(II) sulfate', detail: 'Add a few drops. Violet/lilac indicates peptide bonds.' },
  ],
  lipid: [
    { action: 'add-sample', label: 'Measure the food sample', detail: 'Add a small amount to a clean, dry test tube.' },
    { action: 'add-ethanol', label: 'Add ethanol', detail: 'Add about 2 cm³ of ethanol before adding any water.' },
    { action: 'shake', label: 'Shake to dissolve lipids', detail: 'Mix the sample and ethanol carefully.' },
    { action: 'add-water', label: 'Add distilled water', detail: 'Add water to the ethanolic extract; a cloudy emulsion indicates lipid.' },
  ],
  'vitamin-c': [
    { action: 'add-dcpip', label: 'Measure DCPIP', detail: 'Place a fixed volume of blue DCPIP in a clean tube.' },
    { action: 'add-food-drop', label: 'Add one food drop', detail: 'Add the sample one drop at a time.' },
    { action: 'shake', label: 'Mix and inspect', detail: 'Shake gently after each drop; repeat until the blue colour disappears.' },
  ],
};

export function getFoodTestFlow(test: FoodTestId) {
  return foodTestFlows[test];
}

interface StepGuideProps {
  test: FoodTestId;
  steps: ProcedureStep[];
  onAdvance: (action: ProcedureAction) => void;
  runningTimer?: boolean;
  timerSeconds?: number;
}

const StepGuide: React.FC<StepGuideProps> = ({ test, steps, onAdvance, runningTimer = false, timerSeconds = 0 }) => {
  const flow = foodTestFlows[test];
  const completed = steps.length;
  const isDropTest = test === 'vitamin-c';
  const visibleFlow = isDropTest && completed > 0
    ? [flow[0], { action: 'add-food-drop' as const, label: 'Add another food drop', detail: 'Add one drop at a time; count the drops needed to remove the blue colour.' }, { action: 'shake' as const, label: 'Shake and inspect', detail: 'Mix after each drop. Repeat until DCPIP is colourless.' }]
    : flow;
  const next = isDropTest
    ? completed === 0 ? visibleFlow[0] : steps[completed - 1]?.action === 'add-food-drop' ? visibleFlow[2] : visibleFlow[1]
    : visibleFlow[completed];
  const progress = isDropTest ? Math.min(100, completed ? 66 : 0) : Math.min(100, (completed / flow.length) * 100);

  return (
    <Paper variant="outlined" sx={{ p: 2, borderColor: '#c9d9d3', borderRadius: 1 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1}>
        <Box>
          <Typography variant="subtitle1" fontWeight={800}>Practical sequence</Typography>
          <Typography variant="body2" color="text.secondary">Follow the order; observations depend on technique.</Typography>
        </Box>
        <Chip size="small" label={`${Math.min(completed, flow.length)} / ${flow.length} steps`} />
      </Stack>
      <LinearProgress variant="determinate" value={progress} sx={{ mt: 1.5, mb: 2, height: 6, borderRadius: 1 }} />
      <Stack spacing={1}>
        {visibleFlow.map((item, index) => (
          <Box key={`${item.action}-${index}`} sx={{ display: 'grid', gridTemplateColumns: '28px 1fr auto', gap: 1, alignItems: 'start', opacity: index < completed ? 0.66 : 1 }}>
            <Chip size="small" label={index < completed ? '✓' : index + 1} color={index < completed ? 'success' : index === completed ? 'primary' : 'default'} />
            <Box>
              <Typography variant="body2" fontWeight={700}>{item.label}</Typography>
              <Typography variant="caption" color="text.secondary">{item.detail}</Typography>
            </Box>
            {((isDropTest && item === next) || (!isDropTest && index === completed)) && next && (
              <Button size="small" variant="contained" startIcon={<PlayArrowIcon />} onClick={() => onAdvance(item.action)} disabled={runningTimer}>
                {item.action === 'wait-five-minutes' ? 'Start timer' : 'Do step'}
              </Button>
            )}
          </Box>
        ))}
      </Stack>
      {runningTimer && <Alert severity="info" sx={{ mt: 2 }}>Water-bath timer running: {timerSeconds}s simulated time remaining.</Alert>}
      {!isDropTest && completed >= flow.length && !runningTimer && <Alert severity="success" sx={{ mt: 2 }}>Procedure complete. Inspect the sample and record the observation.</Alert>}
    </Paper>
  );
};

export default StepGuide;